// DailyImportNudgeV3 — surfaces the dedup-analysis result as an
// inline banner when dup_ratio > 0.5. Tells the operator their
// daily-upload pattern is producing duplicate rows + offers a
// one-click 'Configure merge key' that creates a promote_to_table
// trigger with the suggested natural key.
//
// See DAILY_IMPORT_OPTIMIZATION.md for the full strategy. This is
// Layer 5 of the optimisation stack: auto-detect + nudge so the
// operator adopts the already-shipped Layer 4 (BQ MERGE).

import React, { useEffect, useState } from 'react';
import { createTrigger, getDedupAnalysis, type DedupAnalysisResponse } from '../../api';
import { useToast } from '../Toast';

export function DailyImportNudgeV3({ sourceId, dataset, reloadKey, onTriggerCreated }: {
  sourceId: string;
  dataset: string;
  reloadKey: number;
  onTriggerCreated: () => void;
}): React.ReactElement | null {
  const toast = useToast();
  const [analysis, setAnalysis] = useState<DedupAnalysisResponse | null>(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(`v3.daily-nudge.dismissed.${sourceId}`) === '1'; } catch { return false; }
  });
  const [showConfigure, setShowConfigure] = useState(false);
  const [pickedPath, setPickedPath] = useState('');
  const [tableName, setTableName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Cost audit #6: getDedupAnalysis fires 2 BQ scans (5000-row
  // sample + 9-way UNION). Previously every mount + every reloadKey
  // bump = a fresh pair of scans. Now we cache the response per
  // sourceId in sessionStorage for 5 min — re-mount within the
  // window reads from cache. Explicit `reloadKey` bump (operator
  // hits Refresh on the parent) invalidates.
  useEffect(() => {
    let alive = true;
    const cacheKey = `v3.daily-nudge.cache.${sourceId}`;
    const TTL_MS = 5 * 60 * 1000;
    try {
      const raw = sessionStorage.getItem(cacheKey);
      if (raw && reloadKey === 0) {
        const cached = JSON.parse(raw) as { ts: number; data: DedupAnalysisResponse };
        if (Date.now() - cached.ts < TTL_MS) {
          setAnalysis(cached.data);
          return () => { alive = false; };
        }
      }
    } catch { /* corrupt cache → fall through to fetch */ }
    getDedupAnalysis(sourceId)
      .then(r => {
        if (!alive) return;
        setAnalysis(r);
        try { sessionStorage.setItem(cacheKey, JSON.stringify({ ts: Date.now(), data: r })); } catch { /* private mode */ }
      })
      .catch(() => { if (alive) setAnalysis(null); });
    return () => { alive = false; };
  }, [sourceId, reloadKey]);

  if (dismissed || !analysis || analysis.dup_ratio < 0.5 || analysis.sample_size < 20) {
    return null;
  }
  const dupPct = Math.round(analysis.dup_ratio * 100);
  const top = analysis.suggested_merge_keys[0];

  function dismiss(): void {
    try { localStorage.setItem(`v3.daily-nudge.dismissed.${sourceId}`, '1'); } catch { /* private mode */ }
    setDismissed(true);
  }

  async function configureMergeKey(): Promise<void> {
    if (!pickedPath.trim() || !tableName.trim()) {
      toast.show('Pick a merge key and a table name.', { kind: 'warning' });
      return;
    }
    setSubmitting(true);
    try {
      await createTrigger(sourceId, {
        name: `dedupe_promote_${tableName.trim()}`,
        action: {
          type: 'promote_to_table',
          table_name: tableName.trim(),
          merge_key_path: pickedPath.trim(),
        },
      });
      toast.show(
        `Created promote_to_table trigger. Daily uploads will MERGE into \`${dataset}.${tableName}\` keyed on ${pickedPath}.`,
        { kind: 'success' },
      );
      setShowConfigure(false);
      dismiss();
      onTriggerCreated();
    } catch (e) {
      toast.show(`Configure failed: ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div style={{
      padding: '10px 14px',
      background: 'oklch(0.97 0.04 220)',
      border: '1px solid oklch(0.78 0.10 220)',
      borderRadius: 8,
      display: 'flex', flexDirection: 'column', gap: 8,
      fontSize: 12.5, color: 'oklch(0.36 0.14 220)',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 14 }}>📅</span>
        <strong>Daily-import detected</strong>
        <span style={{ color: 'var(--ink-2)' }}>
          {dupPct}% of events repeat ({analysis.sample_size - analysis.unique_payload_hashes}/{analysis.sample_size} sampled).
        </span>
        <span style={{ flex: 1 }} />
        {!showConfigure && (
          <>
            <button
              type="button"
              onClick={() => {
                setPickedPath(top?.path ?? '');
                setTableName(`${top?.path ?? 'records'}_canonical`.replace(/[^a-z0-9]+/g, '_'));
                setShowConfigure(true);
              }}
              className="btn sm primary"
              style={{ padding: '3px 12px', fontSize: 11.5 }}
            >Pick the unique field →</button>
            <button
              type="button"
              onClick={dismiss}
              className="btn sm ghost"
              style={{ padding: '3px 8px', fontSize: 11 }}
              title="Dismiss permanently"
            >×</button>
          </>
        )}
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', lineHeight: 1.5 }}>
        Pick a field that uniquely identifies each row (e.g. <code style={{ fontFamily: 'var(--mono)' }}>entry_id</code>, <code style={{ fontFamily: 'var(--mono)' }}>invoice_id</code>).
        New rows insert, existing rows update — daily uploads stop creating duplicate rows and queries get ~{dupPct}% faster on this source.
      </div>
      {showConfigure && (
        <div style={{
          marginTop: 4, padding: '10px 12px',
          background: 'var(--bg-elev)', border: '1px solid var(--accent-line)', borderRadius: 6,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>Unique field — the value that identifies each row</div>
          {analysis.suggested_merge_keys.length > 0 ? (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {analysis.suggested_merge_keys.map(s => {
                const active = pickedPath === s.path;
                return (
                  <button
                    key={s.path}
                    type="button"
                    onClick={() => setPickedPath(s.path)}
                    style={{
                      padding: '3px 10px', fontSize: 11.5, fontFamily: 'var(--mono)',
                      background: active ? 'var(--accent)' : 'var(--bg)',
                      color: active ? 'white' : 'var(--ink-2)',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--line)'}`,
                      borderRadius: 14, cursor: 'pointer',
                    }}
                    title={`${Math.round(s.coverage * 100)}% coverage · ${Math.round(s.cardinality_ratio * 100)}% unique`}
                  >{s.path}</button>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              No common primary-key fields detected (id, entry_id, ref, broj, …). Type a custom path:
            </div>
          )}
          <input
            value={pickedPath}
            onChange={e => setPickedPath(e.target.value)}
            placeholder="payload field (e.g. entry_id)"
            style={{
              padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12,
              border: '1px solid var(--line)', borderRadius: 4,
              background: 'var(--bg)', color: 'var(--ink)',
            }}
          />
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)', marginTop: 4 }}>Canonical table name</div>
          <input
            value={tableName}
            onChange={e => setTableName(e.target.value.replace(/[^a-zA-Z0-9_]/g, '_'))}
            placeholder="e.g. journal_canonical"
            style={{
              padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 12,
              border: '1px solid var(--line)', borderRadius: 4,
              background: 'var(--bg)', color: 'var(--ink)',
            }}
          />
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={() => setShowConfigure(false)}
              className="btn sm ghost"
              disabled={submitting}
            >Cancel</button>
            <button
              type="button"
              onClick={() => { void configureMergeKey(); }}
              disabled={submitting || !pickedPath.trim() || !tableName.trim()}
              className="btn sm primary"
            >{submitting ? 'Creating…' : 'Create trigger + dismiss'}</button>
          </div>
        </div>
      )}
    </div>
  );
}
